import React, { useEffect, useRef, useState } from 'react';
import { Form, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import CBInputNumber from '../../../components/CBInputNumber';
import dayjs from 'dayjs';
import { T, H_TITLE, H_SUB, SECTION_LABEL } from './declarationTheme';
import { Field, Grid2, Grid3, Input, DatePicker, Select, inputProps, selectProps } from './fields';
import axiosInstance from '../../../variables/api/axiosInstance';
import SelectWithAdd from '../../../components/SelectWithAdd';
import { libellePaysOrganisme } from '../../../variables/paysCedeao';
/* ─────────────────────────────────────────────────────────────
   Étape 3 - Assuré & véhicule
   - Toggle Personne physique / morale
   - Identité assuré (profession paramétrée)
   - Véhicule (marque paramétrée, VT, capacité, passagers, remorque)
   ───────────────────────────────────────────────────────────── */
export default function StepAssureVehicule({ form, embedded = false }) {
  const { t } = useTranslation('declaration');
  const estPersonneMorale = Form.useWatch('estPersonneMorale', form);
  const aRemorque = Form.useWatch('aRemorque', form);
  const assureNom = Form.useWatch('assureNom', form);

  /* Personne morale : le propriétaire du véhicule suit la raison sociale.
     Il reste modifiable - un véhicule en crédit-bail appartient à la société de
     leasing, pas à l'assuré. D'où le garde-fou du bloc déclarant : on n'écrit que
     si le champ est vide, ou s'il contient encore ce qu'on y avait mis (ou déjà la
     raison sociale, cas d'un dossier rouvert). Une saisie manuelle n'est jamais
     écrasée. */
  const proprietaireAuto = useRef(null);
  useEffect(() => {
    const actuel = (form.getFieldValue('proprietaireVehicule') || '').trim();
    if (!estPersonneMorale) {
      // Retour en personne physique : on retire la raison sociale qu'on avait posée.
      if (proprietaireAuto.current != null && actuel === proprietaireAuto.current) {
        form.setFieldValue('proprietaireVehicule', undefined);
      }
      proprietaireAuto.current = null;
      return;
    }
    const raison = (assureNom || '').trim();
    const suitLaRaison = !actuel || actuel === proprietaireAuto.current || actuel === raison;
    if (!suitLaRaison) return;
    if (actuel !== raison) form.setFieldValue('proprietaireVehicule', raison || undefined);
    proprietaireAuto.current = raison;
  }, [estPersonneMorale, assureNom, form]);

  /* Compagnies membres - la remorque peut être couverte par un contrat distinct
     de celui du tracteur, il faut donc pouvoir désigner son assureur. */
  const [compagnies, setCompagnies] = useState([]);
  useEffect(() => {
    let annule = false;
    axiosInstance.get('/v1/organismes/type/COMPAGNIE_MEMBRE')
      .then(r => { if (!annule) setCompagnies(r.data?.data || []); })
      .catch(() => { if (!annule) setCompagnies([]); });
    return () => { annule = true; };
  }, []);

  /* Meme raison qu'a l'etape « Contrat & assureurs » : deux compagnies de pays
     differents peuvent porter le meme nom. La valeur reste la raison sociale
     seule - c'est ce que la colonne `remorque_assureur` stocke - mais le pays est
     affiche pour que le choix ne soit pas aveugle. */
  const optionsCompagnies = compagnies
    .map(c => {
      const pays = libellePaysOrganisme(c);
      return {
        value: c.raisonSociale,
        recherche: `${c.raisonSociale} ${pays} ${c.codePays || ''} ${c.codePaysBCB || ''}`,
        tri: c.raisonSociale || '',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.raisonSociale}
            </span>
            {pays && <span style={{ flexShrink: 0, fontSize: 12, color: T.inkMuted }}>{pays}</span>}
          </div>
        ),
      };
    })
    .sort((a, b) => a.tri.localeCompare(b.tri));

  const Wrapper = embedded ? React.Fragment : 'div';
  const wrapperProps = embedded ? {} : { style: { maxWidth: 760 } };

  return (
    <Wrapper {...wrapperProps}>
      {!embedded && (
        <>
          <h1 style={H_TITLE}>{t('assureVehicule.titre')}</h1>
          <p style={H_SUB}>
            {t('assureVehicule.sousTitre')}
          </p>
        </>
      )}

      {/* ══════ Nature de l'assuré ══════ */}
      <div style={{ marginTop: embedded ? 0 : 40 }}>
        <div style={SECTION_LABEL}>{t('assureVehicule.natureAssure')}</div>

        <Form.Item name="estPersonneMorale" valuePropName="checked" noStyle>
          <Switch checkedChildren={t('assureVehicule.personneMorale')} unCheckedChildren={t('assureVehicule.personnePhysique')} />
        </Form.Item>
        <div style={{ fontSize: 11.5, color: T.inkMuted, marginTop: 6 }}>
          {estPersonneMorale
            ? t('assureVehicule.aidePersonneMorale')
            : t('assureVehicule.aidePersonnePhysique')}
        </div>
      </div>

      {/* ══════ Identité ══════ */}
      <div style={{ marginTop: 24 }}>
        <div style={SECTION_LABEL}>{estPersonneMorale ? t('assureVehicule.identiteMorale') : t('assureVehicule.identitePersonnelle')}</div>

        <Grid2>
          <Field
            name="assureNom"
            label={estPersonneMorale ? t('assureVehicule.raisonSociale') : t('assureVehicule.nom')}
            rules={[{ required: true, message: estPersonneMorale ? t('assureVehicule.raisonSocialeRequise') : t('assureVehicule.nomRequis') }]}
          >
            <Input {...inputProps} placeholder={estPersonneMorale ? 'SOTRAG SA' : 'LAKMON'} maxLength={200} />
          </Field>

          {!estPersonneMorale && (
            <Field name="assurePrenom" label={t('assureVehicule.prenom')}>
              <Input {...inputProps} placeholder="Adjikamba" maxLength={200} />
            </Field>
          )}
        </Grid2>

        {!estPersonneMorale && (
          <Grid2>
            <Field
              name="assureProfession"
              label={t('assureVehicule.profession')}
            >
              <SelectWithAdd
                categorie="PROFESSION"
                placeholder={t('assureVehicule.selectionnerProfession')}
                addLabel={t('assureVehicule.ajouterProfession')}
                style={{ width: '100%' }}
                size="large"
              />
            </Field>
            <div />
          </Grid2>
        )}

        <Grid2>
          <Field name="assureTelephone" label={t('assureVehicule.telephone')}>
            <Input {...inputProps} placeholder="+228 ..." maxLength={30} />
          </Field>

          <Field name="assureAdresse" label={t('assureVehicule.adresse')}>
            <Input {...inputProps} placeholder={t('assureVehicule.adresseExemple')} maxLength={200} />
          </Field>
        </Grid2>
      </div>

      {/* ══════ Véhicule ══════ */}
      <div style={{ marginTop: 28 }}>
        <div style={SECTION_LABEL}>{t('assureVehicule.vehiculeAssure')}</div>

        <Grid2>
          <Field
            name="immatriculation"
            label={t('assureVehicule.immatriculation')}
            rules={[{ required: true, message: t('assureVehicule.immatriculationRequise') }]}
          >
            <Input
              {...inputProps}
              placeholder="TG-1234-AB"
              style={{ ...inputProps.style, fontFamily: T.fontMono, letterSpacing: '0.04em' }}
              maxLength={30}
            />
          </Field>

          <Field
            name="marqueVehicule"
            label={t('assureVehicule.marqueModele')}
          >
            <SelectWithAdd
              categorie="MARQUE"
              valueKey="valeur"
              placeholder={t('assureVehicule.selectionnerMarque')}
              addLabel={t('assureVehicule.ajouterMarque')}
              style={{ width: '100%' }}
              size="large"
            />
          </Field>
        </Grid2>

        <Grid3>
          <Field
            name="prochaineVT"
            label={t('assureVehicule.prochaineVisiteTechnique')}
          >
            <DatePicker
              style={{ ...inputProps.style, width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={t('commun.formatDate')}
              disabledDate={(d) => d && d.isBefore(dayjs().subtract(5, 'year'))}
            />
          </Field>

          <Field name="capaciteVehicule" label={t('assureVehicule.nombrePlaces')}>
            <CBInputNumber style={{ ...inputProps.style, width: '100%' }} min={1} max={150} placeholder="1" />
          </Field>

          <Field name="nbPersonnesABord" label={t('assureVehicule.personnesTransportees')}>
            <CBInputNumber style={{ ...inputProps.style, width: '100%' }} min={0} max={150} placeholder="0" />
          </Field>
        </Grid3>

        <Grid2>
          <Field
            name="proprietaireVehicule"
            label={t('assureVehicule.proprietaire')}
          >
            <Input {...inputProps} placeholder={t('assureVehicule.proprietaireExemple')} maxLength={200} />
          </Field>

          <Field name="aRemorque" label={t('assureVehicule.vehiculeAvecRemorque')}>
            <Form.Item name="aRemorque" valuePropName="checked" noStyle>
              <Switch
                checkedChildren={t('assureVehicule.avecRemorque')}
                unCheckedChildren={t('assureVehicule.vehiculeSimple')}
                style={{ background: aRemorque ? T.brand : undefined }}
              />
            </Form.Item>
          </Field>
        </Grid2>
      </div>

      {/* ══════ Remorque ══════
          Un attelage est composé de deux engins : la remorque a sa propre
          plaque, sa propre marque et, très souvent, son propre contrat. */}
      {aRemorque && (
        <div style={{ marginTop: 8 }}>
          <div style={SECTION_LABEL}>{t('assureVehicule.remorqueAttelee')}</div>

          <Grid2>
            <Field
              name="remorqueImmatriculation"
              label={t('assureVehicule.immatriculationRemorque')}
              rules={[{ required: true, message: t('assureVehicule.immatriculationRemorqueRequise') }]}
            >
              <Input
                {...inputProps}
                placeholder="TG-5678-CD"
                style={{ ...inputProps.style, fontFamily: T.fontMono, letterSpacing: '0.04em' }}
                maxLength={50}
              />
            </Field>

            <Field name="remorqueMarque" label={t('assureVehicule.marqueRemorque')}>
              <SelectWithAdd
                categorie="MARQUE"
                valueKey="valeur"
                placeholder={t('assureVehicule.selectionnerMarque')}
                addLabel={t('assureVehicule.ajouterMarque')}
                style={{ width: '100%' }}
                size="large"
              />
            </Field>
          </Grid2>

          <Grid2>
            <Field name="remorqueModele" label={t('assureVehicule.modeleRemorque')}>
              <Input {...inputProps} placeholder={t('assureVehicule.modeleRemorqueExemple')} maxLength={100} />
            </Field>

            <Field name="remorqueAssureur" label={t('assureVehicule.compagnieRemorque')}>
              <Select
                {...selectProps}
                showSearch
                allowClear
                placeholder={t('assureVehicule.compagnieAssurance')}
                filterOption={(saisie, option) =>
                  (option?.recherche || '').toLowerCase().includes(saisie.toLowerCase())}
                options={optionsCompagnies}
              />
            </Field>
          </Grid2>
        </div>
      )}
    </Wrapper>
  );
}
